import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell
} from "recharts";
import {
  Plus, TrendingUp, AlertTriangle, CheckCircle, Trash2, ChevronRight,
  FolderOpen, Building2, Users, Shield, Activity, Zap, Target
} from "lucide-react";

import { API } from "../lib/api";

function getRiskColor(level) {
  if (!level) return { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400" };
  const l = level.toLowerCase();
  if (l.includes("healthy")) return { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" };
  if (l.includes("moderate")) return { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" };
  if (l.includes("high")) return { bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-500" };
  return { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" };
}

function getScoreColor(score) {
  if (score >= 80) return "text-emerald-600";
  if (score >= 60) return "text-amber-600";
  if (score >= 40) return "text-orange-600";
  return "text-red-600";
}

function getBarColor(score) {
  if (score >= 4) return "#10B981";
  if (score >= 3) return "#F59E0B";
  return "#EF4444";
}

function pillarLabel(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Org-Wide Dashboard (Admin/Executive) ─────────────────────────────────
function OrgDashboard({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/dashboard/org`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!data || data.error) return null;

  const radarData = data.pillar_averages
    ? Object.entries(data.pillar_averages).map(([k, v]) => ({ pillar: pillarLabel(k), score: v, fullMark: 5 }))
    : [];

  const riskData = data.risk_breakdown
    ? Object.entries(data.risk_breakdown).map(([k, v]) => ({ name: k, count: v }))
    : [];

  const riskColors = { Healthy: "#10B981", "Moderate Risk": "#F59E0B", "High Risk": "#F97316", Critical: "#EF4444" };

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-slide-up stagger-1">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500">Org PIQ Score</span>
            <Activity className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
          </div>
          <div className={`font-outfit text-4xl font-black ${getScoreColor(data.avg_score)}`}>{data.avg_score}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500">Assessments</span>
            <FolderOpen className="w-4 h-4 text-slate-500" strokeWidth={1.5} />
          </div>
          <div className="font-outfit text-4xl font-black text-slate-900">{data.total_assessments}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500">Employees</span>
            <Users className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
          </div>
          <div className="font-outfit text-4xl font-black text-slate-900">{data.total_employees}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500">At Risk</span>
            <AlertTriangle className="w-4 h-4 text-red-500" strokeWidth={1.5} />
          </div>
          <div className="font-outfit text-4xl font-black text-red-600">
            {(data.risk_breakdown?.["High Risk"] || 0) + (data.risk_breakdown?.Critical || 0)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Org Radar Chart */}
        {radarData.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-2">
            <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">Organization Health Radar</h2>
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="pillar" tick={{ fontFamily: "IBM Plex Sans", fontSize: 10, fill: "#6B7280" }} />
                <PolarRadiusAxis angle={30} domain={[0, 5]} tickCount={6} tick={{ fontSize: 9, fill: "#9CA3AF" }} />
                <Radar name="Score" dataKey="score" stroke="#0F172A" fill="#0F172A" fillOpacity={0.12} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Risk Insight — Bottom Pillars */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-3">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-4 h-4 text-red-500" />
              <h2 className="font-outfit text-base font-bold text-slate-900">Risk Insights</h2>
              <span className="font-ibm-plex text-xs text-slate-400">weakest areas</span>
            </div>
            {data.bottom_pillars?.map((bp, i) => (
              <div key={bp.pillar} className="flex items-center gap-3 mb-3">
                <span className="font-outfit text-lg font-black text-slate-200 w-6">#{i + 1}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-ibm-plex text-sm font-medium text-slate-700">{pillarLabel(bp.pillar)}</span>
                    <span className="font-outfit text-sm font-bold" style={{ color: getBarColor(bp.score) }}>{bp.score}/5</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(bp.score / 5) * 100}%`, background: getBarColor(bp.score) }} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Risk Distribution */}
          {riskData.some(r => r.count > 0) && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-4">
              <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">Risk Distribution</h2>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={riskData} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis type="number" tick={{ fontFamily: "IBM Plex Sans", fontSize: 11, fill: "#9CA3AF" }} />
                  <YAxis type="category" dataKey="name" tick={{ fontFamily: "IBM Plex Sans", fontSize: 11, fill: "#6B7280" }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {riskData.map((entry) => (
                      <Cell key={entry.name} fill={riskColors[entry.name] || "#94A3B8"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Team Breakdown */}
      {data.teams?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-5">
          <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">Team Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.teams.map((t) => (
              <div key={t.name} className="border border-gray-200 rounded-lg p-4">
                <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">{t.name}</p>
                <div className="flex items-end gap-2">
                  <span className={`font-outfit text-2xl font-black ${getScoreColor(t.avg_score)}`}>{t.avg_score}</span>
                  <span className="font-ibm-plex text-xs text-slate-400 mb-0.5">{t.count} assessment{t.count !== 1 ? "s" : ""}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Assessments */}
      {data.recent_assessments?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden animate-slide-up stagger-6">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-outfit text-base font-bold text-slate-900">Recent Assessments</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {data.recent_assessments.slice(0, 8).map((p) => {
              const rc = getRiskColor(p.risk_level);
              return (
                <div key={p.id} onClick={() => navigate(`/project/${p.id}`)}
                  className="px-6 py-3 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors">
                  <span className={`font-outfit text-lg font-black w-12 ${getScoreColor(p.piq_score)}`}>{p.piq_score}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-outfit font-semibold text-slate-900 text-sm truncate">{p.name}</p>
                    <p className="font-ibm-plex text-xs text-slate-400">{p.employee_name} · {new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-ibm-plex font-medium ${rc.bg} ${rc.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${rc.dot}`} />
                    {p.risk_level}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Individual Dashboard ─────────────────────────────────────────────────
function IndividualDashboard({ projects, navigate, deleteProject, user }) {
  const stats = {
    total: projects.length,
    healthy: projects.filter((p) => p.risk_level === "Healthy").length,
    atRisk: projects.filter((p) => p.risk_level !== "Healthy").length,
    avgScore: projects.length ? Math.round(projects.reduce((s, p) => s + p.piq_score, 0) / projects.length) : 0
  };
  const isIndividual = user?.account_type === "individual";
  const { refreshUser } = useAuth();

  const upgradeToOrg = async () => {
    try {
      await axios.post(`${API}/api/account/switch-to-org`, {}, { withCredentials: true });
      if (refreshUser) await refreshUser();
      navigate("/admin/setup");
    } catch (err) { console.error(err); }
  };

  return (
    <>
      {/* Upgrade to Org banner for individual users */}
      {isIndividual && !user?.org_id && (
        <div className="mb-6 bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-5 flex items-center justify-between animate-slide-up">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-outfit font-bold text-white text-sm">Scale to a team?</p>
              <p className="font-ibm-plex text-xs text-slate-300">Upgrade to Organization for team dashboards, employee management & alerts.</p>
            </div>
          </div>
          <button data-testid="upgrade-to-org-btn" onClick={upgradeToOrg}
            className="bg-white text-slate-900 font-outfit font-semibold px-5 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm flex-shrink-0">
            Upgrade to Org
          </button>
        </div>
      )}
      {projects.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 animate-slide-up stagger-1">
          {[
            { label: "Total Projects", value: stats.total, icon: FolderOpen, color: "text-blue-600" },
            { label: "Avg PIQ Score", value: stats.avgScore, icon: TrendingUp, color: getScoreColor(stats.avgScore) },
            { label: "Healthy", value: stats.healthy, icon: CheckCircle, color: "text-emerald-600" },
            { label: "At Risk", value: stats.atRisk, icon: AlertTriangle, color: "text-red-600" }
          ].map((s) => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500">{s.label}</span>
                <s.icon className={`w-4 h-4 ${s.color}`} strokeWidth={1.5} />
              </div>
              <div className={`font-outfit text-3xl font-black ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {projects.length === 0 ? (
        <div data-testid="empty-state" className="bg-white border border-gray-200 rounded-xl p-16 text-center animate-fade-in">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FolderOpen className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
          </div>
          <h3 className="font-outfit text-xl font-bold text-slate-900 mb-2">No projects yet</h3>
          <p className="font-ibm-plex text-slate-500 text-sm mb-6">Create your first project analysis to get started with SYNQ intelligence.</p>
          <button data-testid="create-first-project-btn" onClick={() => navigate("/project/new")}
            className="bg-slate-900 text-white font-outfit font-semibold px-6 py-3 rounded-lg hover:bg-slate-800 transition-colors">
            Create First Analysis
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-4 animate-slide-up stagger-2">
            <h2 className="font-outfit text-base font-bold text-slate-900">Recent Projects</h2>
            <button onClick={() => navigate("/pulse")} data-testid="pulse-check-btn"
              className="flex items-center gap-2 border border-blue-200 text-blue-600 bg-blue-50 font-outfit font-medium px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors text-sm">
              <Activity className="w-4 h-4" /> Weekly Pulse
            </button>
          </div>
          {projects.map((p, i) => {
            const rc = getRiskColor(p.risk_level);
            return (
              <div key={p.id} data-testid={`project-card-${p.id}`}
                onClick={() => navigate(`/project/${p.id}`)}
                className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-4 cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all animate-slide-up"
                style={{ animationDelay: `${i * 0.05 + 0.1}s` }}>
                <div className="w-16 h-16 flex-shrink-0 flex flex-col items-center justify-center bg-slate-50 rounded-lg border border-gray-200">
                  <span className={`font-outfit text-xl font-black ${getScoreColor(p.piq_score)}`}>{p.piq_score}</span>
                  <span className="font-ibm-plex text-xs text-slate-400">/ 100</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-outfit font-semibold text-slate-900 text-base truncate">{p.name}</h3>
                    <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-ibm-plex font-medium ${rc.bg} ${rc.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${rc.dot}`} />
                      {p.risk_level}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs font-ibm-plex text-slate-400">
                    <span>{p.project_type}</span>
                    <span>·</span>
                    <span>{p.failure_probability}% failure risk</span>
                    <span>·</span>
                    <span>{new Date(p.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button data-testid={`delete-project-${p.id}`} onClick={(e) => deleteProject(p.id, e)}
                    className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ChevronRight className="w-5 h-5 text-slate-300" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ─── Main Dashboard ────────────────────────────────────────────────────────
export default function DashboardPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("personal"); // personal | org
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOrgUser = user?.account_type === "organization";
  const needsOrgSetup = isOrgUser && !user?.org_id;

  useEffect(() => {
    axios.get(`${API}/api/projects`, { withCredentials: true })
      .then(({ data }) => setProjects(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Default to org view for org-type admins with an org
  useEffect(() => {
    if (isAdmin && user?.org_id && isOrgUser) setView("org");
  }, [isAdmin, user?.org_id, isOrgUser]);

  const deleteProject = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project?")) return;
    await axios.delete(`${API}/api/projects/${id}`, { withCredentials: true });
    setProjects((prev) => prev.filter((p) => p.id !== id));
  };

  // Full-screen onboarding for admins without an org
  if (needsOrgSetup) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center animate-slide-up">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="font-outfit text-3xl font-black text-slate-900 mb-3">Welcome to SYNQ</h1>
            <p className="font-ibm-plex text-base text-slate-500 mb-2 max-w-md mx-auto">
              Set up your organization to unlock team assessments, employee management, and organizational intelligence.
            </p>
            <p className="font-ibm-plex text-sm text-slate-400 mb-8">
              Configure your industry, company size, and methodology so SYNQ can adapt its diagnostics to your context.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 text-left">
              {[
                { step: "1", title: "Organization Profile", desc: "Name, industry, team size, methodology" },
                { step: "2", title: "Import Employees", desc: "CSV upload or add manually" },
                { step: "3", title: "Run Assessments", desc: "Role-based diagnostic intelligence" },
              ].map((s) => (
                <div key={s.step} className="border border-gray-200 rounded-xl p-4">
                  <div className="w-7 h-7 bg-slate-900 rounded-full flex items-center justify-center text-white text-xs font-outfit font-bold mb-2">{s.step}</div>
                  <p className="font-outfit text-sm font-bold text-slate-900">{s.title}</p>
                  <p className="font-ibm-plex text-xs text-slate-500 mt-0.5">{s.desc}</p>
                </div>
              ))}
            </div>

            <button
              data-testid="setup-org-btn"
              onClick={() => navigate("/admin/setup")}
              className="bg-slate-900 text-white font-outfit font-semibold px-8 py-3.5 rounded-lg hover:bg-slate-800 transition-colors text-base"
            >
              Set Up Organization
            </button>
            <p className="font-ibm-plex text-xs text-slate-400 mt-4">
              You can also run individual assessments without an organization.{" "}
              <button onClick={() => navigate("/project/new")} className="text-blue-600 hover:text-blue-800 font-medium">
                Skip for now
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 animate-slide-up">
          <div>
            <h1 className="font-outfit text-3xl font-black text-slate-900">Dashboard</h1>
            <p className="font-ibm-plex text-sm text-slate-500 mt-1">
              {isOrgUser && user?.org_id ? "Organization intelligence overview" : `${projects.length} project${projects.length !== 1 ? "s" : ""} analyzed`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isOrgUser && user?.org_id && (
              <div className="flex border border-gray-200 rounded-lg p-0.5 bg-gray-50 mr-2">
                <button data-testid="view-org" onClick={() => setView("org")}
                  className={`px-3 py-1.5 text-sm font-outfit font-medium rounded-md transition-all ${view === "org" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  <Building2 className="w-3.5 h-3.5 inline mr-1" /> Org
                </button>
                <button data-testid="view-personal" onClick={() => setView("personal")}
                  className={`px-3 py-1.5 text-sm font-outfit font-medium rounded-md transition-all ${view === "personal" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  Personal
                </button>
              </div>
            )}
            <button data-testid="new-project-btn" onClick={() => navigate("/project/new")}
              className="flex items-center gap-2 bg-slate-900 text-white font-outfit font-semibold px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-colors">
              <Plus className="w-4 h-4" /> New Analysis
            </button>
          </div>
        </div>

        {/* Admin Setup Banner */}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : view === "org" && isOrgUser && user?.org_id ? (
          <OrgDashboard navigate={navigate} />
        ) : (
          <IndividualDashboard projects={projects} navigate={navigate} deleteProject={deleteProject} user={user} />
        )}
      </div>
    </div>
  );
}
